(ns ui.view.grid-opensearch.totals
  "The totals of the number columns of the grid: the row at the bottom of the grid,
   and the cells of a group row. See `common.opensearch.totals`."
  (:require
   [common.localization :as loc]
   [common.opensearch.days-since :as days-since]
   [oops.core :as oops]
   [reagent.core :as r]
   [ui.view.opensearch-value.format :as value-format]))

(def ^:private total-data-types
  #{:data.type/currency-value
    :data.type/number
    :data.type/integer
    :data.type/long
    :data.type/percentage
    :data.type/multiplier})

(def ^:private average-data-types
  "A percentage or a multiple of many rows means something as an average only."
  #{:data.type/percentage
    :data.type/multiplier})

(def totals-key
  "The key of the texts of the totals in the data of a total row or a group row."
  "__totals")

(defn total-kind
  "\"sum\" or \"avg\": the field schema can say it (`:field/total-kind`), and a
   percentage and a multiple are averaged."
  [{:keys [data-type total-kind]}]
  (cond
    (some? total-kind) (name total-kind)
    (contains? average-data-types data-type) "avg"
    :else "sum"))

(defn request-totals
  "The totals that the grid asks for: one for each number column. A column that
   shows the days since a time has no total: its value depends on today. An
   amount of a link to many records has no total by currency, see
   `common.opensearch.totals/single-valued-path?`."
  [column-defs]
  (->> column-defs
       (keep (fn [{:keys [colId field-data-type flat-field currency-flat-field] :as column-def}]
               (when (and (contains? total-data-types field-data-type)
                          (string? flat-field)
                          (nil? (days-since/since-alias flat-field))
                          (not (and (= :data.type/currency-value field-data-type)
                                    (false? (:single-valued-path? column-def)))))
                 (cond-> {:id    colId
                          :field flat-field
                          :kind  (total-kind {:data-type  field-data-type
                                              :total-kind (:total-kind column-def)})}
                   (and (= :data.type/currency-value field-data-type)
                        (string? currency-flat-field))
                   (assoc :currency-field currency-flat-field)))))
       (vec)))

(defn totals-texts
  "The texts of the totals `parsed` (see `common.opensearch.totals/parse`) by the id
   of their column. `label-col-id` gets the word Total, when it has no total."
  [{:keys [column-defs parsed default-currency number-multiplier label-col-id]}]
  (let [data-type-by-col-id (into {} (map (juxt :colId :field-data-type)) column-defs)]
    (cond-> (->> parsed
                 (keep (fn [[col-id total]]
                         (when-let [text (value-format/total-text {:data-type         (get data-type-by-col-id col-id)
                                                                   :total             total
                                                                   :default-currency  default-currency
                                                                   :number-multiplier number-multiplier})]
                           [col-id text])))
                 (into {}))
      (some? label-col-id)
      (update label-col-id #(or % (loc/tr :grid-totals/total))))))

(defn total-row?
  "True for the row at the bottom of the grid and for a group row: their cells show
   totals."
  [params-js]
  (boolean (or (= "bottom" (oops/oget params-js "?node.?rowPinned"))
               (oops/oget params-js "?node.?group"))))

(defn total-cell-renderer
  "The cell of a total row: the text of the total of its column."
  [params-js]
  (let [col-id (oops/oget params-js "?colDef.?colId")
        text (some-> (oops/oget params-js "?data")
                     (js->clj)
                     (get totals-key)
                     (get col-id))]
    (r/as-element
     [:span {:class "tw2-font-semibold tabular-nums"} (or text "")])))
